import React, { useRef, useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { ChatImageUpload } from '@/components/chat/ChatImageUpload';
import ReactMarkdown from 'react-markdown';
import { Send, Plus, Mic, Edit3, BookOpen, Edit2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useWebsites } from '@/context/WebsitesContext';
import { usePostThemes } from '@/context/PostThemesContext';
import { toast } from 'sonner';
import { updateContentWithChat } from '@/api/chatEndpoints';
import { useChatImageUpload } from '@/hooks/useChatImageUpload';

type ChatMessage = {
  role: 'assistant' | 'user';
  content: string;
  _id?: number;
  _streaming?: boolean;
  _typing?: boolean;
  _loading?: boolean;
};

interface ContentEditChatProps {
  postThemeId: string;
  postTitle: string;
  postContent: string;
  onContentUpdate: (newContent: string) => void;
  onClose: () => void;
  currentPlatform?: string | null;
  onPlatformChange?: (platform: string | null) => void;
  postType?: 'post_theme' | 'social';
  platform?: string;
}

export default function ContentEditChat({
  postThemeId,
  postTitle,
  postContent,
  onContentUpdate,
  onClose,
  currentPlatform = null,
  onPlatformChange,
  postType = 'post_theme',
  platform
}: ContentEditChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: `I'm here to help you edit your post "${postTitle}". You can ask me to:
      
- Improve the content structure
- Enhance specific sections
- Add more details or examples
- Fix grammar and style issues
- Make the tone more engaging
- Add call-to-actions
- Optimise for SEO

What would you like me to help you with?`
    }
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const { currentWebsite } = useWebsites();
  const { updatePostTheme, fetchPostThemes } = usePostThemes();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  
  // Editing state management
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(postContent);
  const [selectedText, setSelectedText] = useState('');
  
  // Image upload management
  const { selectedImage, handleImageSelect, handleImageRemove, uploadImageToStorage, isUploading } = useChatImageUpload();
  const [stagedChange, setStagedChange] = useState<
    | { type: 'selection'; replacement: string; selection: string }
    | { type: 'full'; replacement: string }
    | null
  >(null);

  // Attempt to extract the replacement slice from a full-document update
  const extractReplacementFromFullUpdate = (
    originalFull: string,
    originalSelection: string,
    updatedFull: string
  ): string | null => {
    if (!originalSelection || !originalFull || !updatedFull) return null;
    const selIndex = originalFull.indexOf(originalSelection);
    if (selIndex === -1) return null;
    const prefix = originalFull.slice(0, selIndex);
    const suffix = originalFull.slice(selIndex + originalSelection.length);

    // Find prefix and suffix anchors in the updated full content
    const prefixIdx = updatedFull.indexOf(prefix);
    if (prefixIdx !== 0) {
      // If prefix not at 0, still try to locate the last occurrence, as content may have whitespace diffs
      const altPrefixIdx = updatedFull.lastIndexOf(prefix);
      if (altPrefixIdx === -1) return null;
      const afterPrefix = updatedFull.slice(altPrefixIdx + prefix.length);
      const suffixIdxRel = afterPrefix.indexOf(suffix);
      if (suffixIdxRel === -1) return null;
      return afterPrefix.slice(0, suffixIdxRel);
    }
    const afterPrefix = updatedFull.slice(prefix.length);
    const suffixIdxRel = afterPrefix.indexOf(suffix);
    if (suffixIdxRel === -1) return null;
    return afterPrefix.slice(0, suffixIdxRel);
  };

  // Compute a compact before/after preview window for full-document updates
  const computeChangePreview = (
    originalFull: string,
    updatedFull: string,
    windowSize: number = 240
  ): { beforeHtml: string; afterHtml: string } | null => {
    if (!originalFull || !updatedFull) return null;
    const minLen = Math.min(originalFull.length, updatedFull.length);
    let start = 0;
    while (start < minLen && originalFull[start] === updatedFull[start]) start++;
    if (start === originalFull.length && start === updatedFull.length) return null; // identical
    let endOrig = originalFull.length - 1;
    let endUpd = updatedFull.length - 1;
    while (endOrig >= start && endUpd >= start && originalFull[endOrig] === updatedFull[endUpd]) {
      endOrig--;
      endUpd--;
    }
    const beforeStart = Math.max(0, start - windowSize);
    const beforeEnd = Math.min(originalFull.length, endOrig + windowSize + 1);
    const afterStart = Math.max(0, start - windowSize);
    const afterEnd = Math.min(updatedFull.length, endUpd + windowSize + 1);
    const beforeHtml = originalFull.slice(beforeStart, beforeEnd).trim();
    const afterHtml = updatedFull.slice(afterStart, afterEnd).trim();
    if (!beforeHtml && !afterHtml) return null;
    return { beforeHtml, afterHtml };
  };

  // Expose a safe way to set selected text without altering layout
  const setSelectionForChat = (text: string) => {
    const clean = (text || '').trim();
    setSelectedText(clean);
    try {
      if (clean) localStorage.setItem('selected_excerpt', clean);
    } catch {}
  };

  // Load any pre-stashed selection on mount
  useEffect(() => {
    try {
      const stored =
        localStorage.getItem('selected_excerpt_for_chat') ||
        localStorage.getItem('selected_excerpt');
      if (stored && stored.trim()) {
        setSelectedText(stored.trim());
      }
    } catch {}
  }, []);

  // Live-sync selection from right pane via storage events
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (e.key === 'selected_excerpt' || e.key === 'selected_excerpt_for_chat') {
        const val = (e.newValue || '').trim();
        setSelectedText(val);
      }
    };
    window.addEventListener('storage', onStorage);
    const onCustom = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail;
        if (detail && typeof detail.text === 'string') {
          setSelectedText(detail.text.trim());
        }
      } catch {}
    };
    window.addEventListener('content_edit_selection', onCustom as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('content_edit_selection', onCustom as EventListener);
    };
  }, []);

  // Do not auto-clear selection; keep it visible for the chat even if not found verbatim
  // This avoids races when formatting differs slightly from the plain-text selection
  useEffect(() => {
    // noop: intentionally keep selection until user clears
  }, [editedContent, selectedText]);

  const clearSelectionForChat = () => {
    setSelectedText('');
    try { localStorage.removeItem('selected_excerpt'); } catch {}
    try { localStorage.removeItem('selected_excerpt_for_chat'); } catch {}
  };

  const clearStagedChange = () => setStagedChange(null);

  const applyStagedChange = () => {
    if (!stagedChange) return;
    if (stagedChange.type === 'selection') {
      // Delegate to drawer via DOM Range for exact HTML replacement
      try {
        window.dispatchEvent(new CustomEvent('cgai_apply_selection', { detail: { replacement: stagedChange.replacement } }));
        toast.message('Applying to selected area…');
      } catch {}
    } else {
      setEditedContent(stagedChange.replacement);
      onContentUpdate(stagedChange.replacement);
      toast.success('Content updated');
      clearStagedChange();
      clearSelectionForChat();
    }
  };

  // Sync applied DOM replacement back from drawer
  useEffect(() => {
    const onApplied = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as { content?: string } | undefined;
        const content = (detail?.content || '').toString();
        if (content) {
          setEditedContent(content);
          onContentUpdate(content);
          toast.success('Selection updated');
          clearStagedChange();
          clearSelectionForChat();
        }
      } catch {}
    };
    window.addEventListener('cgai_apply_selection_done', onApplied as EventListener);
    return () => window.removeEventListener('cgai_apply_selection_done', onApplied as EventListener);
  }, []);

  // Update editedContent when postContent prop changes (but not when editing)
  useEffect(() => {
    if (!isEditing) {
      setEditedContent(postContent);
    }
  }, [postContent, isEditing]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  // Focus input after sending message
  useEffect(() => {
    if (!sending && inputRef.current) {
      inputRef.current.focus();
    }
  }, [sending]);

  // Auto-resize the textarea so it grows with content up to a max height
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = 160; // px
    const newHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${newHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [input]);

  /**
   * AI rewrite only the selected portion of text, preserving surrounding content.
   * Refactored from ContentEditorDrawer for use without changing this component's layout.
   */
  const handleAiRewriteSelection = async () => {
    if (!selectedText.trim() || !postThemeId) return;
    try {
      toast.info('Rewriting selected text…');
      const res = await updateContentWithChat(
        postThemeId,
        {
          userMessage:
            'Please rewrite the selected passage to improve clarity and tone while preserving meaning. Keep the rest unchanged.',
          selectedText: selectedText,
        },
        editedContent
      );

      if ((res as any)?.success) {
        const updatedFull = (
          ((res as any)?.updatedContent as string | undefined) ||
          ''
        ).trim();
        // Prefer server-provided replacement if present
        let replacement = (
          ((res as any)?.replacement as string | undefined) ||
          ''
        ).trim();

        if (!replacement && updatedFull) {
          replacement = extractReplacementFromFullUpdate(editedContent, selectedText, updatedFull) || '';
        }

        if (!replacement) {
          toast.error((res as any)?.message || 'Rewrite failed');
          return;
        }

        // Immediately apply via DOM range in the drawer
        try {
          window.dispatchEvent(new CustomEvent('cgai_apply_selection', { detail: { replacement } }));
          toast.success('Applied to selection');
          clearStagedChange();
          clearSelectionForChat();
        } catch {}
      } else {
        toast.error((res as any)?.message || 'Rewrite failed');
      }
    } catch (e) {
      console.error('AI rewrite error:', e);
      toast.error('Failed to rewrite selection');
    }
  };

  // Handle saving edited content
  const handleSaveEdit = async () => {
    console.log("Saving content:", {
      editedContent,
      postType,
      platform,
      postThemeId,
    });
    try {
      if (postType === 'social') {
        // Update some_posts table for social media content
        const { error } = await supabase.from("some_posts").update({
          content: editedContent,
          updated_at: new Date().toISOString(),
        }).eq('id', postThemeId);

        if (error) throw error;

        onContentUpdate(editedContent);
      } else {
        // Save to main post theme content
        if (postThemeId) {
          const success = await updatePostTheme(
            postThemeId,
            {
              post_content: editedContent,
            },
            false
          ); // Don't show toast here as we'll show our own

          if (!success) {
            throw new Error("Failed to update post theme");
          }

          // Refresh post themes to ensure the UI is updated
          await fetchPostThemes();
        }
        onContentUpdate(editedContent);
      }
      setIsEditing(false);
      console.log("Content saved successfully");
      toast.success("Content saved successfully");
    } catch (err) {
      console.error("Error saving content:", err);
      toast.error("Failed to save content");
    }
  };

  const handleSend = async () => {
    if ((!input.trim() && !selectedImage) || sending || isUploading) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);

    // Add user message (with image indicator if present)
    const newUserMessage: ChatMessage = {
      role: 'user',
      content: selectedImage ? `${userMessage} [Image: ${selectedImage.file.name}]` : userMessage,
      _id: Date.now()
    };

    setMessages(prev => [...prev, newUserMessage]);

    // Add loading message
    const loadingMessage: ChatMessage = {
      role: 'assistant',
      content: '',
      _id: Date.now() + 1,
      _loading: true
    };

    setMessages(prev => [...prev, loadingMessage]);

    // Upload image if present
    let uploadedImageUrl: string | undefined;
    if (selectedImage && currentWebsite?.id) {
      console.log('[ContentEditChat] Uploading image before sending message');
      const uploadResult = await uploadImageToStorage(currentWebsite.id);
      if (uploadResult) {
        uploadedImageUrl = uploadResult.url;
        console.log('[ContentEditChat] Image uploaded successfully:', uploadedImageUrl);
      } else {
        console.error('[ContentEditChat] Failed to upload image');
        toast.error('Failed to upload image');
      }
    }

    try {
      // Auto-select likely target section when user asks for specific parts (e.g., conclusion) without a selection
      let effectiveSelected = selectedText?.trim() || '';
      if (!effectiveSelected) {
        const lower = userMessage.toLowerCase();
        const wantsConclusion = /\b(conclusion|wrap\s*up|closing|final\s*thoughts)\b/.test(lower);
        if (wantsConclusion && editedContent) {
          try {
            const html = editedContent;
            const headingMatch = html.match(/<h[1-6][^>]*>[^<]*conclusion[^<]*<\/h[1-6]>/i);
            if (headingMatch) {
              const headingEnd = (headingMatch.index || 0) + headingMatch[0].length;
              const rest = html.slice(headingEnd);
              const nextHeadingIdxRel = rest.search(/<h[1-6][^>]*>/i);
              const sectionEnd = nextHeadingIdxRel !== -1 ? headingEnd + nextHeadingIdxRel : html.length;
              const sectionHtml = html.slice(headingEnd, sectionEnd).trim();
              if (sectionHtml) {
                effectiveSelected = sectionHtml;
                setSelectedText(sectionHtml);
                try {
                  localStorage.setItem('selected_excerpt', sectionHtml);
                  localStorage.setItem('selected_excerpt_for_chat', sectionHtml);
                } catch {}
              }
            }
          } catch {}
        }
      }
      // Get user session for authentication
      const { data: sessionData } = await supabase.auth.getSession();

      const payload = {
        postThemeId,
        postTitle,
        postContent: editedContent,
        userMessage,
        websiteId: (
          currentWebsite?.id ||
          localStorage.getItem('onboardingWebsiteId') ||
          localStorage.getItem('website_id') ||
          localStorage.getItem('websiteId') ||
          undefined
        ),
        websiteUrl: currentWebsite?.url,
        postType: postType,
        platform: platform,
        selectedExcerpt: (() => {
          const cleaned = (effectiveSelected || '').trim();
          // Only send a selection if it still exists in the current content
          if (cleaned && editedContent?.includes(cleaned)) return cleaned;
          return undefined;
        })(),
        imageUrl: uploadedImageUrl // Add uploaded image URL if available
      };

      console.log('[ContentEditChat] POST content-edit-chat payload', {
        hasAuth: !!sessionData?.session?.access_token,
        websiteId: payload.websiteId,
        hasSelectedExcerpt: !!payload.selectedExcerpt,
        hasImage: !!payload.imageUrl,
        postThemeId: payload.postThemeId
      });

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/content-edit-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionData?.session?.access_token ? { 'Authorization': `Bearer ${sessionData.session.access_token}` } : {}),
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ ...payload, apply: false })
      });

      // Handle non-OK HTTP responses with meaningful error messages
      if (!response.ok) {
        let serverErrorMessage = `${response.status} ${response.statusText}`;
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const errorBody = await response.json();
            serverErrorMessage = errorBody?.error || errorBody?.message || serverErrorMessage;
          } else {
            const text = await response.text();
            if (text) serverErrorMessage = `${serverErrorMessage} - ${text}`;
          }
        } catch {}
        console.error('[ContentEditChat] Non-OK response from function', serverErrorMessage);
        throw new Error(serverErrorMessage);
      }

      const data = await response.json();

      if (data.success) {
        // Build a single assistant message depending on whether we staged a change
        let assistantContent = data.response || '';

        let staged = false;
        if (data.updatedContent) {
          const effectiveSelected = payload.selectedExcerpt;
          const updatedFull = data.updatedContent;
          if (effectiveSelected) {
            let replacement = extractReplacementFromFullUpdate(editedContent, effectiveSelected, updatedFull);
            // Fallback to server-provided replacement if extraction fails or no diff
            if (!replacement || updatedFull === editedContent) {
              const serverReplacement = (data as any)?.replacement as string | undefined;
              if (serverReplacement && serverReplacement.trim()) {
                replacement = serverReplacement.trim();
              }
            }
            if (replacement && replacement.trim()) {
              setStagedChange({ type: 'selection', replacement: replacement.trim(), selection: effectiveSelected });
              assistantContent = `Here's a rewrite for your selection. Would you like me to apply it?\n\n${replacement.trim()}`;
              staged = true;
            } else {
              // Stage full update with a compact before/after preview
              const preview = computeChangePreview(editedContent, updatedFull);
              setStagedChange({ type: 'full', replacement: updatedFull });
              assistantContent = preview
                ? `I've prepared an updated version of the content. See preview below. Shall I apply these changes?`
                : `I couldn't reliably isolate just the selection. I have a full content update ready. Apply it?`;
              staged = true;
            }
          } else {
            const preview = computeChangePreview(editedContent, updatedFull);
            setStagedChange({ type: 'full', replacement: updatedFull });
            assistantContent = preview
              ? `I've prepared an updated version of the content. See preview below. Shall I apply these changes?`
              : `I've prepared an updated version of the content. Shall I apply these changes?`;
            staged = true;
          }
        }

        if (!staged) {
          const fallback = (data.response || '').trim();
          assistantContent = fallback || 'I could not determine any changes to apply. Please select the text you want changed or describe the change more specifically.';
          if (!fallback) {
            toast.message('No changes proposed. Select text or be more specific to see a preview.');
          }
        }

        // Replace loading message with a single assistant message
        setMessages(prev => {
          const withoutLoading = prev.filter(msg => !msg._loading);
          return [...withoutLoading, {
            role: 'assistant',
            content: assistantContent,
            _id: Date.now() + 2
          }];
        });

        // Clear uploaded image after processing (do not auto-apply or clear selection; await user confirmation)
        handleImageRemove();
      } else {
        const details = data?.error || 'Failed to get response';
        console.error('[ContentEditChat] Function returned unsuccessful payload', data);
        throw new Error(details);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Remove loading message and add error message
      setMessages(prev => {
        const withoutLoading = prev.filter(msg => !msg._loading);
        return [...withoutLoading, {
          role: 'assistant',
          content: `Sorry, something went wrong: ${error instanceof Error ? error.message : 'Unexpected error'}`,
          _id: Date.now() + 2
        }];
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-full w-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div className="flex items-center gap-2">
          <Edit3 className="h-4 w-4 text-brand-green-border" />
          <h3 className="font-medium">Edit Post Content</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-8 w-8 p-0"
        >
          ×
        </Button>
      </div>

      {/* Chat messages */}
      <ScrollArea ref={scrollRef as any} className="flex-1 px-4">
        <div className="space-y-4 pb-4 pt-4">
          {messages.map((m, idx) => (
            <div
              key={m._id || idx}
              className={`flex ${m.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
            >
              <div className={`max-w-xl rounded-2xl px-4 py-3 text-sm shadow-sm transition-all duration-300 select-text ${
                m.role === 'assistant' 
                  ? 'bg-background border border-brand-green-border text-left' 
                  : 'bg-background text-foreground border border-transparent text-right'
              }`}>
                {m.role === 'assistant' ? (
                  <div className="prose prose-sm max-w-none prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-li:text-foreground">
                    {m._loading ? (
                      <div className="flex items-center space-x-2">
                        <div className="relative w-6 h-6">
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-4 h-4 text-brand-green-border animate-grow-leaf">
                              🌱
                            </div>
                          </div>
                        </div>
                        <span className="text-muted-foreground text-sm">
                          {selectedText ? 'Editing your selection...' : 'Editing your content...'}
                        </span>
                      </div>
                    ) : (
                      <ReactMarkdown 
                        components={{
                          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                          li: ({ children }) => <li className="text-sm">{children}</li>,
                          strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                          a: ({ href, children }) => (
                            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline">{children}</a>
                          )
                        }}
                      >
                        {m.content}
                      </ReactMarkdown>
                    )}
                  </div>
                ) : (
                  m.content
                )}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
      </ScrollArea>

      {/* Chat input */}
      <div className="p-4 border-t bg-background/50">
        {stagedChange && (
          <div className="mb-3 rounded-md border border-brand-green-border bg-muted/50 p-3">
            <div className="mb-2 text-sm font-medium">
              {stagedChange.type === 'selection' ? 'Apply rewrite to your selected text?' : 'Apply proposed content update?'}
            </div>
            {stagedChange.type === 'selection' && (
              <div className="mb-2 max-h-32 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">
                {stagedChange.replacement}
              </div>
            )}
            {stagedChange.type === 'full' && (() => {
              const preview = computeChangePreview(editedContent, stagedChange.replacement);
              if (!preview) return null;
              return (
                <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <div className="mb-1 text-xs font-medium text-muted-foreground">Before (excerpt)</div>
                    <div className="max-h-40 overflow-auto rounded border bg-background p-2 text-xs" dangerouslySetInnerHTML={{ __html: preview.beforeHtml }} />
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-medium text-muted-foreground">After (excerpt)</div>
                    <div className="max-h-40 overflow-auto rounded border bg-background p-2 text-xs" dangerouslySetInnerHTML={{ __html: preview.afterHtml }} />
                  </div>
                </div>
              );
            })()}
            <div className="flex gap-2">
              <Button size="sm" onClick={applyStagedChange}>Apply changes</Button>
              <Button size="sm" variant="secondary" onClick={() => {
                // Keep staged, let user refine instruction
                setInput((prev) => prev || 'Can you revise that further?');
              }}>Revise</Button>
              <Button size="sm" variant="ghost" onClick={clearStagedChange}>Discard</Button>
            </div>
          </div>
        )}
        {selectedText && (
          <div className="mb-2 flex justify-end">
            <div className={`inline-flex max-w-full items-start gap-2 rounded-md border bg-muted/60 px-3 py-2 align-top ${sending ? 'animate-pulse' : ''}`}>
              <span className="text-xs italic text-muted-foreground break-words break-all">
                editing selected text!
              </span>
              <button
                type="button"
                onClick={clearSelectionForChat}
                className="-mr-1 ml-1 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-muted text-muted-foreground"
                aria-label="Clear selected text"
                title="Clear selected text"
              >
                ×
              </button>
            </div>
          </div>
        )}
        
        {/* Image preview */}
        {selectedImage && (
          <div className="mb-3 flex items-start gap-3">
            <ChatImageUpload
              onImageSelect={handleImageSelect}
              onImageRemove={handleImageRemove}
              selectedImage={selectedImage}
              disabled={sending || isUploading}
            />
            <div className="flex-1 text-sm text-muted-foreground">
              <p className="font-medium">{selectedImage.file.name}</p>
              <p className="text-xs">{(selectedImage.file.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-2 rounded-full border border-brand-green-border bg-muted/30 px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-brand-green-border/50">
          {!selectedImage && (
            <ChatImageUpload
              onImageSelect={handleImageSelect}
              onImageRemove={handleImageRemove}
              selectedImage={selectedImage}
              disabled={sending || isUploading}
            />
          )}

          <Textarea
            ref={inputRef}
            placeholder={
              selectedImage 
                ? "Describe what you'd like to do with this image..." 
                : selectedText 
                ? 'How would you like to improve the selected text?' 
                : 'How would you like to improve this content?'
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { 
              if (e.key === 'Enter' && !e.shiftKey) { 
                e.preventDefault(); 
                handleSend(); 
              } 
            }}
            className="flex-1 bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-sm resize-none min-h-[36px] max-h-40"
            disabled={sending || isUploading}
            rows={1}
          />

          <button
            type="button"
            className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted transition-colors"
            aria-label="Voice"
          >
            <Mic className="h-4 w-4 text-muted-foreground" />
          </button>

          <Button
            onClick={handleSend}
            disabled={sending || isUploading || (!input.trim() && !selectedImage)}
            className="h-9 w-9 rounded-full p-0 shadow-sm"
            title="Send"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
